/**
 * /guides/how-to-track-wedding-vendors-payments
 *
 * "How to Keep Track of Wedding Vendors and Payments" — the tenth guide, and
 * the operational half of the pair that closes with the ninth. The checklist
 * guide answers WHEN the work happens; this one answers how you hold on to what
 * you have committed to once it starts happening.
 *
 * ── The differentiator, which is the whole piece ──────────────────────────
 * The search landscape splits this subject three ways and does each one
 * separately: generic vendor checklists, "wedding payment schedule" articles,
 * and budget spreadsheets. None of them is what a couple actually needs, which
 * is one record per vendor carrying the whole commitment. The article is built
 * on that chain, stated once as an ordered list in "Create One Record for Each
 * Vendor" and then walked section by section:
 *
 *   vendor -> booking status -> contract -> agreed services -> payment
 *   schedule -> paid -> remaining -> next action -> final confirmation
 *
 * Four things are kept deliberately distinct, because conflating them is the
 * defect the article exists to fix:
 *
 *   - VENDOR tracking    who you have booked and where each one stands
 *   - CONTRACT tracking  what was agreed, in detail, beyond the price
 *   - PAYMENT tracking   which amounts fall due when, and what has cleared
 *   - and BUDGETING, which is none of the above and gets a section saying so
 *     ("Vendor Payments Are Not the Same as Your Wedding Budget").
 *
 * An edit that turns any section into a standalone tip, or that lets the budget
 * and the vendor tracker blur back together, removes the reason this article
 * exists.
 *
 * ── Length, and the three merges that bought it ───────────────────────────
 * The first draft ran to 3,832 rendered words against a house range of roughly
 * 1,850-2,850, because the commission asked for seventeen sections and around
 * ninety enumerated items. It came down to thirteen sections by MERGING pairs
 * that were saying the same thing twice, never by dropping a subject:
 *
 *   1. "Record the Contract" absorbed "Keep the Practical Details With the
 *      Vendor". Their two lists overlapped on four items outright — coverage
 *      hours, venue access, overtime, cancellation terms — which is the tell
 *      that they were one section. The merged list is thirteen items covering
 *      both, commercial terms and day-of operations together, which is also how
 *      they arrive: in one document, at one moment.
 *   2. "Build the Payment Schedule" absorbed "Track Deposits, Installments and
 *      Final Balances Separately". Both demonstrated the same point with a
 *      different prop — one a 3,000 contract against a 600 deposit, the other a
 *      three-installment agreement. They are now ONE worked example doing both
 *      jobs: three rows that sum to the contracted total, with the
 *      contracted/paid/remaining triple read straight off it.
 *   3. "Start With One Master Vendor List" absorbed "A Vendor Tracker You Can
 *      Actually Maintain". The eight columns that section named were a strict
 *      SUBSET of the eleven fields already listed here, so it was restating the
 *      record it had just been given. What was genuinely its own — the vendor
 *      row / payment row relationship, and "add a column when you need one" —
 *      moved up into this section. Its worked photographer row went: the
 *      EUR 3,000 example in the payment-schedule section demonstrates the same
 *      arithmetic, better, and in the place a reader meets it first.
 *
 * Everything the compression brief protected survived: the
 * contract-versus-generic-schedule distinction, paid/remaining/next-due, change
 * tracking, payment proof, tracker-versus-budget, the final review, the product
 * bridge, and all five FAQs. Every mandated list is intact except where a merge
 * de-duplicated it. Anything added here should replace something rather than
 * extend a list.
 *
 * ── The hedges, which are load-bearing ────────────────────────────────────
 * Payment terms are contractual, not conventional, and the article may never
 * imply otherwise. Every example is marked as an example where it is given:
 *
 *   - No percentage is attached to any vendor type. Not venues, not
 *     photographers, not anyone. "Deposit" and "final balance" appear only as
 *     things a couple's own agreement may or may not contain.
 *   - No universal number of installments. The three-payment illustration is
 *     followed immediately by a sentence saying three is not a standard.
 *   - No universal final-payment deadline. The final-review section says "in
 *     the final weeks" and tells the reader to work backwards from the earliest
 *     deadline in their own agreements.
 *   - "Enter the dates and amounts from the agreement you actually signed" is
 *     stated twice, in the payment-schedule section and in the FAQ, because it
 *     is the single claim this guide most needs to get right.
 *
 * Not legal advice, and a note says so: the guide records what was agreed, it
 * does not interpret it. No etiquette, no tipping conventions, nothing
 * jurisdiction-specific asserted as universal.
 *
 * ── The claims this guide is allowed to make ──────────────────────────────
 * All in the closing section, all true of the shipped product, and all already
 * published on /wedding-planning:
 *
 *   - "Agapaé's Planning Hub and Éros are part of the Heirloom plan."
 *     True at `api/constants/planFeatures.mjs`: both hang off
 *     `invitationProduct`, which is `pro` (Heirloom) only.
 *   - planning is divided into categories, each holding its own vendors,
 *     tasks, costs, documents and open decisions
 *   - a vendor card carries the contact person, contact details and notes, with
 *     a status that follows the conversation (ORGANIZER_VENDOR_STATUSES:
 *     researching, contacted, awaiting_quote, quoted, booked, completed,
 *     passed) and an activity history beneath it (ORGANIZER_ACTIVITY_TYPES)
 *   - a cost carries a label, an amount, a due date and the vendor it belongs
 *     to, and is either due or paid  (OrganizerPaymentSchema)
 *   - the totals — total cost, paid, outstanding, budget remaining — and
 *     payments past their date are flagged  (`isOverduePayment` drives the red
 *     `overdue_payment` health reason in `utils/organizerCompute.mjs`)
 *   - dated items, payments included, appear on a calendar  (the
 *     `/:eventId/organizer/calendar` route normalizes dated unpaid payments)
 *   - contracts, quotes and receipts can be filed as documents against the
 *     vendor they came from  (OrganizerDocumentSchema carries a `vendorId`)
 *   - Éros reads what is there and makes a change when asked to
 *
 * And four denials, each a real product boundary rather than modesty: Agapaé is
 * not a vendor directory and contacts, books and pays nobody; it sends no
 * reminders about vendor payments; it is not accounting software or invoicing;
 * and it does not stand in for the signed agreements or the reader's own record
 * of what they have paid. Éros suggests, the couple decides.
 *
 * That third denial is deliberately scoped to VENDOR payments rather than left
 * bare. There is no such job — the only scheduled mail in `api/services/` is
 * the gallery retention reminder — but Stripe payment-receipt and
 * payment-failed mails DO go out, about the couple's own Agapaé subscription.
 * An unqualified 'sends no payment reminders' would therefore have been false.
 *
 * ── Boundaries with the guides it links to ────────────────────────────────
 * `wedding-planning-checklist` owns the order of the whole plan; this guide
 * hands off to it once, in the closing section, and never re-states a phase.
 * `how-to-organize-wedding-guest-list` owns the headcount; it is linked once,
 * at the only point where it genuinely matters here — vendor quantities that
 * are priced per person. Three internal hrefs in total, none used more than
 * twice.
 *
 * ── Structure ─────────────────────────────────────────────────────────────
 * Thirteen sections, unnumbered as in every guide since the third. `steps` (an
 * ordered list) is used only where the order is real: the record chain, the
 * status progression, and the three-installment illustration. The tracker
 * "table" the brief sketches is the eleven-field list in the first section plus
 * the vendor-row / payment-row sentence under it — the block vocabulary in
 * `guideModel.js` has no table type, and adding one would mean changing both
 * renderers and the generator.
 *
 * No copy in this file may contain "&", "<" or ">". The generator escapes them
 * correctly, but `guides.seo.test.mjs` compares against tag-stripped HTML
 * without decoding entities, so a string containing one is reported missing
 * from a document that in fact carries it.
 */

const howToTrackWeddingVendorsPaymentsEn = {
  slug: "how-to-track-wedding-vendors-payments",

  title: "How to Keep Track of Wedding Vendors and Payments",
  metaTitle: "How to Keep Track of Wedding Vendors and Payments | Agapaé",
  metaDescription:
    "Learn how to track wedding vendors, contracts, deposits, balances and payment dates so you know what is booked, paid and still outstanding.",

  eyebrow: "Wedding planning",

  hero: {
    src: "/assets/images/bride_shoes.jpg",
    // Describes the photograph, not the keyword: a quiet preparation frame in
    // black and white, someone kneeling to fasten the ankle strap of the
    // bride's shoe while she stands in her gown. It is the work that happens
    // before anyone sees it, which is what this article is about. Nothing is
    // described here that is not in the frame.
    alt: "A black and white photograph of someone kneeling to fasten the ankle strap of a bride's embellished shoe while she stands in her gown, only their hands and the hem of the dress in frame",
    width: 3000,
    height: 2000,
  },

  // Absent until the guide is genuinely published. Adding one means adding the
  // other, and `dateModified` may not precede `datePublished`.
  // datePublished: "YYYY-MM-DD",
  // dateModified: "YYYY-MM-DD",

  intro: [
    `A wedding does not arrive as one bill. It arrives as a dozen separate agreements, each with its own contact, its own price, its own dates and its own idea of when it wants to be paid.`,
    `The difficulty is rarely the number of vendors. It is that everything about them ends up somewhere different: the price in an email, the deposit in a bank statement, the arrival time in a message thread, the cancellation terms in a document nobody has reopened since signing.`,
    `This guide is about the record that fixes that: one entry per vendor, the agreement behind it, the payments that come out of it, and the status that says what needs attention this week.`,
  ],

  sections: [
    {
      // Absorbed the old "A Vendor Tracker You Can Actually Maintain": its
      // eight columns were a subset of the eleven fields below, so it restated
      // this section. Only what was genuinely its own came up — the vendor-row
      // / payment-row relationship, and adding columns as you need them.
      heading: "Start With One Master Vendor List",
      content: [
        `A vendor list earns its place the first time it answers a question you would otherwise go looking for. Six of them:`,
        {
          type: "list",
          items: [
            `Which vendors have we actually booked, and which are still only quotes?`,
            `What did we agree to with each one?`,
            `What is each vendor going to cost in total?`,
            `How much have we already paid?`,
            `How much is still outstanding?`,
            `What is the next payment, and when is it due?`,
          ],
        },
        `From memory those answers are approximately right, which is a different thing from right. The last four cannot be answered at all without one place to add them up.`,
        `A workable list carries, for each vendor:`,
        {
          type: "list",
          items: [
            `the vendor or business name`,
            `the category of work they are doing`,
            `the person you actually deal with`,
            `their contact details`,
            `booking status`,
            `contract status, and where the signed copy lives`,
            `the contracted total`,
            `what has been paid so far`,
            `the remaining balance`,
            `the next payment and its date`,
            `a note saying what you are waiting on`,
          ],
        },
        `Two tables, one relationship: a row per vendor carrying those fields, and underneath it a row per payment carrying the vendor, the label, the amount, the due date and its status. Every total on the vendor row should be reproducible from the payment rows.`,
        `No particular tool is required: a spreadsheet, a planning tool and a shared document all work. What matters is that there is only one, that it is updated when something changes rather than when you remember, and that columns get added as you need them rather than all at once.`,
      ],
    },
    {
      heading: "Create One Record for Each Vendor",
      content: [
        `The unit of a vendor tracker is the vendor, not the payment and not the category. Open a record as soon as a business becomes a real possibility, so the quote from March is still findable in September.`,
        `Weddings commonly involve some mixture of:`,
        {
          type: "list",
          items: [
            `the venue`,
            `catering, where the venue does not provide it`,
            `photography`,
            `videography`,
            `flowers and decoration`,
            `music or entertainment`,
            `a planner, or a coordinator for the day`,
            `hair and makeup`,
            `transportation`,
            `the cake`,
            `rentals, such as furniture, tableware or lighting`,
            `stationery and printing`,
          ],
        },
        `No wedding needs all of them. Categories only group records; the record is the thing, and this is what it accumulates, in order:`,
        {
          type: "steps",
          items: [
            `You add the vendor, with a category and a contact.`,
            `You move them through a booking status as the conversation progresses.`,
            `Once booked, you record the contract: what it covers and what it costs.`,
            `You write down the services and quantities it actually includes.`,
            `You take the payment schedule out of that contract and enter every payment on it.`,
            `You mark each payment paid as it is genuinely completed.`,
            `The remaining balance is the contracted total minus what has been paid.`,
            `A next action sits on the record whenever the ball is in your court.`,
            `In the final weeks, you confirm the whole record back with the vendor.`,
          ],
        },
      ],
    },
    {
      heading: "Track Where Each Vendor Stands",
      content: [
        `A price with no status attached is not information. Two vendors quoting the same amount are in very different positions if one has countersigned and the other has not replied in weeks.`,
        `A simple progression is enough:`,
        {
          type: "steps",
          items: [
            `Inquiry. You have made contact; nothing is agreed.`,
            `Quote. You have a price, and it is still yours to accept or decline.`,
            `Booked. You have agreed to work together.`,
            `Contract signed. The agreement exists in writing, signed by both sides.`,
            `In progress. Booked, partly paid, and the work is under way.`,
            `Finalized. Everything agreed, everything paid, every detail confirmed.`,
          ],
        },
        `Adapt them to how you work; what matters is applying them consistently, so five vendors marked booked are genuinely in the same position. Keep the gap between booked and contract signed open on purpose: a verbal yes and a signed agreement behave very differently later.`,
      ],
    },
    {
      // Absorbed the old "Keep the Practical Details With the Vendor", which
      // lived here all along. Four of its nine items duplicated this list
      // outright, and both were describing one act — reading the agreement once
      // and writing down everything in it that is not the price.
      heading: "Record the Contract, Not Just the Price",
      content: [
        `The price is the least useful thing in a wedding contract: it is the part you were always going to remember. The rest is what you will be reconstructing in the final month.`,
        `When a vendor is booked, take from the agreement and keep on their record:`,
        {
          type: "list",
          items: [
            `the contracted amount, and how it is stated`,
            `what that amount includes, service by service`,
            `how many people, portions or items are covered`,
            `arrival and setup time, and how long their coverage lasts`,
            `the access, power or space they need at the venue`,
            `the dates and times you have agreed`,
            `what they will deliver afterwards, and roughly when`,
            `whether their team needs a meal, and for how many`,
            `the payment terms, in the agreement's own words`,
            `what overtime or extra work costs if the day runs long`,
            `the cancellation and postponement terms`,
            `anything particular to this vendor you would not think to ask twice`,
            `the questions you have asked and not yet had answered`,
          ],
        },
        {
          type: "note",
          text: `This is a record of what you agreed, not a reading of it. If a term matters and you are not sure what it means, ask the vendor, or take proper advice.`,
        },
        `That last item is the one most often missing and the most valuable. A written-down question gets answered; one you are carrying around gets remembered on the morning of the wedding.`,
        {
          type: "p",
          text: [
            `Several depend on your final headcount rather than the vendor: covers, portions, chairs, seats in a car. Those come out of your guest list and the replies to it, and `,
            {
              text: "How to Organize a Wedding Guest List",
              href: "/guides/how-to-organize-wedding-guest-list",
            },
            ` covers holding that together.`,
          ],
        },
      ],
    },
    {
      // Absorbed the old "Track Deposits, Installments and Final Balances
      // Separately", which demonstrated the same point with a second prop. One
      // worked example now does both jobs — three rows that sum to the
      // contracted total, and the contracted/paid/remaining triple off it.
      heading: "Build the Payment Schedule From the Contract",
      content: [
        `This is where vendor tracking fails, and it fails quietly: a deposit gets paid and the rest of the commitment stays inside a document until it becomes urgent. When a vendor is booked, go through the agreement and enter every payment it names straight away. For each one record:`,
        {
          type: "list",
          items: [
            `the vendor it belongs to`,
            `a label saying which payment it is`,
            `the amount`,
            `the due date, taken from the agreement`,
            `its status: still due, or paid`,
            `the date it was actually paid`,
            `a payment method or reference, if you will need to find it again`,
            `a note for anything unusual about it`,
          ],
        },
        `Every date and amount comes from the agreement you signed. Do not fill a payment schedule in from a generic wedding timeline: terms vary from vendor to vendor and contract to contract, and a date you assumed is a date nobody owes you.`,
        `One row per payment, not one per vendor. Suppose a signed agreement is for €3,000, payable in three parts, and you have paid the first:`,
        {
          type: "steps",
          items: [
            `First payment, €600: paid, with the date it cleared.`,
            `Second payment, €1,200: due, with its date.`,
            `Third payment, €1,200: due, with its date.`,
          ],
        },
        `That gives the three numbers that matter: €3,000 contracted, €600 paid, €2,400 remaining. It also puts the later payments in front of you months before they are urgent, alongside every other vendor's — the only reliable way to spot two large balances falling in the same fortnight.`,
        `Three is not a standard, and neither is any other number. Some agreements are settled in full on booking, some in two parts, some tie a payment to a milestone rather than a date. Whatever yours says, give each payment its own row: money paid and money committed are two different numbers, and only one will surprise you.`,
      ],
    },
    {
      heading: "Always Show Paid, Remaining and Next Due",
      content: [
        `Four numbers do most of the work:`,
        {
          type: "list",
          items: [
            `total contracted, across every agreement you have signed`,
            `total paid, meaning what has actually left your account`,
            `remaining, which is contracted minus paid, per vendor and overall`,
            `next payment due, with its amount and its date`,
          ],
        },
        `The first three tell you where you stand; the fourth tells you what to do.`,
        `Most vendor lists end up sorted by category, because that is how they were built. Operationally that is the wrong sort: a €400 balance due next Friday needs you more than a €4,000 balance due in five months. Keep one view sorted by next payment date, ignoring category, and read it top down — that is a list of what to deal with, in order.`,
      ],
    },
    {
      heading: "Record Changes When the Agreement Changes",
      content: [
        `Very few weddings are delivered exactly as first agreed. Things move:`,
        {
          type: "list",
          items: [
            `an extra hour of coverage`,
            `more guests than the quote was priced for`,
            `an upgraded package, or a service added late`,
            `extra equipment, staff or transport`,
            `a change to the schedule, the room or the venue`,
            `something removed, and a price that should come down with it`,
          ],
        },
        `Each usually changes two things: the agreed total, and the payment schedule that came out of it. Update both, and note what changed and when.`,
        `The failure mode is specific: the new arrangement lives in a message thread while the tracker shows the old figures, and weeks later the tracker is what everyone plans from. Where a change is material — money, hours, headcount, dates — have the vendor confirm it in writing, even briefly by email.`,
      ],
    },
    {
      heading: "Keep the Proof, Not Just the Number",
      content: [
        `A tracker records that a payment happened. It is not the evidence that it happened.`,
        `Worth keeping in full:`,
        {
          type: "list",
          items: [
            `quotes and invoices`,
            `receipts and payment confirmations`,
            `bank transfer references`,
            `the signed agreement, and any revised version of it`,
            `correspondence in which something was agreed or changed`,
          ],
        },
        `Keep them somewhere durable that you control, and will still have access to a year afterwards. Point the tracker at them rather than replacing them: the tracker is the index, the documents are the record.`,
      ],
    },
    {
      heading: "Review Upcoming Payments Regularly",
      content: [
        `A tracker is only as current as the last time someone looked at it. A short, repeated review keeps it honest:`,
        {
          type: "list",
          items: [
            `check the next 30 days often enough that nothing surprises you`,
            `look 60 or 90 days out for the shape of what is coming`,
            `mark a payment paid only once it has genuinely completed, with the date`,
            `chase anything past its date, or anything you cannot account for`,
            `check that every change agreed since the last review is in the tracker`,
          ],
        },
        `How often depends on how much is happening: monthly through the middle of planning, weekly once final balances start landing together. No tracker chases anything on your behalf — the review is the mechanism, and a good tracker only makes it quick.`,
      ],
    },
    {
      heading: "Vendor Payments Are Not the Same as Your Wedding Budget",
      content: [
        `These two records get merged constantly, and they answer different questions.`,
        { type: "subheading", text: "The vendor tracker" },
        `Who you have booked, what you agreed, what it costs, what has been paid, what remains and what falls due next. Per commitment, and what you work from week to week.`,
        { type: "subheading", text: "The wedding budget" },
        `What the wedding is expected to cost across categories, and how spending compares with that plan. Per category, and what you work from when deciding what you can afford.`,
        `They overlap at one point: a contracted total is a committed cost whether or not any of it has been paid, so a budget fed only by payments already made understates what you owe. €600 of paid deposits against €3,000 of signed commitments looks comfortable and is not. A good system links the two, but the vendor side is what tells you what to do this week.`,
      ],
    },
    {
      heading: "Do a Final Vendor Review Before the Wedding",
      content: [
        `In the final weeks, go through the tracker vendor by vendor and confirm that:`,
        {
          type: "list",
          items: [
            `the agreement on file is the current version, including anything changed since signing`,
            `the remaining balance is what you think it is, and the vendor agrees`,
            `every final payment is completed or scheduled, on the date the agreement names`,
            `any quantity that depends on the guest count is confirmed, by that vendor's deadline`,
            `arrival, setup and finish times are agreed with each vendor`,
            `every open question on the record has been closed`,
            `you have a number for each vendor that will be answered on the day`,
            `whoever is running the day has the same information you do`,
          ],
        },
        `When to do this depends on the deadlines in your own agreements — some vendors want final numbers weeks ahead, some a few days — so work backwards from the earliest, not from a date someone else's checklist picked.`,
      ],
    },
    {
      heading: "Common Vendor-Tracking Mistakes",
      content: [
        `Most of what goes wrong is ordinary rather than dramatic:`,
        {
          type: "list",
          items: [
            `keeping vendor information only in email, where nothing can be totalled`,
            `recording the deposit and never the balance behind it`,
            `taking payment dates from a generic timeline instead of the signed agreement`,
            `tracking the total budget but not when each payment falls due`,
            `agreeing a change and leaving the tracker on the old figures`,
            `marking a payment paid when it was only arranged`,
            `relying on memory for vendor details such as arrival times, inclusions and access`,
            `reaching the final weeks without confirming anything back`,
          ],
        },
        `Almost all share one cause: the tracker was filled in when it was created and not when things changed. A record you have stopped trusting is the same as none.`,
      ],
    },
    {
      // The brief closes with a separate Agapaé section, which is what the
      // `related` block below already renders as an H2. Two H2s reading the
      // same words is a defect, and every earlier guide resolved it the same
      // way: this section is named for what it does, and "Where this fits in
      // Agapaé" stays with the related block.
      heading: "Keeping the Vendor Plan in One Place",
      content: [
        {
          // The only plan-availability claim in the guide, in the same place
          // and the same block type every earlier guide put theirs.
          type: "note",
          text: `Agapaé's Planning Hub and Éros are part of the Heirloom plan.`,
        },
        `In Agapaé, planning is divided into the parts a wedding is made of, each holding its own vendors, tasks, costs, documents and open decisions. Every vendor has a card of their own — the person you deal with, their contact details, your notes — and a status that follows the real conversation, from researching through to booked or passed, with the calls, emails and meetings you log underneath.`,
        `Costs are recorded much as this guide describes them: a label, an amount, a due date and the vendor they belong to, each still due or paid. Four totals stay in front of you — total cost, paid, outstanding and budget remaining — and anything past its date is flagged. Dated items appear on a calendar, payments included, and contracts, quotes and receipts can be filed against the vendor they came from.`,
        `Éros is the planning assistant inside that space. It reads what is there and answers from it, and when you ask, it makes the change itself: record a payment, update a vendor, add the task that follows. It suggests and you decide.`,
        `It does not choose anyone for you. Agapaé is not a vendor directory, nothing is contacted, booked or paid through it, and it sends no reminders about vendor payments. It is not accounting software, and does not stand in for your signed agreements or your own record of what you have paid.`,
        {
          type: "p",
          text: [
            `The planning side is described on `,
            { text: "Wedding Planning", href: "/wedding-planning" },
            `. Vendor tracking is one piece of a larger plan, and `,
            {
              text: "Wedding Planning Checklist: What to Organize and When",
              href: "/guides/wedding-planning-checklist",
            },
            ` covers the order the rest of it happens in.`,
          ],
        },
      ],
    },
  ],

  faqHeading: "Frequently Asked Questions",

  // Visible copy, always expanded. `faqSchema` is deliberately not set, exactly
  // as on the nine earlier guides: Google restricts FAQ rich results to
  // government and health sites, so the markup would buy nothing while
  // committing us to a second copy of every answer.
  faq: [
    {
      q: `What should I include in a wedding vendor tracker?`,
      a: `One record per vendor: the business name, the category, the person you deal with and their contact details, the booking status, whether a contract is signed, the contracted total, what has been paid, the remaining balance, and the next payment with its date. Keep the practical details beside them: arrival time, coverage hours, what is included, any open question.`,
    },
    {
      q: `How do I track wedding vendor payments?`,
      a: `Take the payment schedule out of each agreement as soon as it is signed, and give every payment its own row with the vendor, a label, the amount, the due date and a status. Mark one paid only once it has actually completed. Then keep three totals per vendor — contracted, paid and remaining — and one view of every unpaid payment sorted by due date.`,
    },
    {
      q: `Should I track deposits and final payments separately?`,
      a: `Yes. A single paid figure says something has been settled without saying what is still coming or when. If an agreement contains more than one payment, give each its own due date and status, so a balance is visible months before it becomes urgent and you can see when two large ones fall close together.`,
    },
    {
      q: `When are wedding vendor payments usually due?`,
      a: `That depends entirely on the vendor and the contract, and it should never be inferred from a generic wedding timeline. Terms vary by vendor, country, season and agreement: some are settled in full on booking, some in installments, some tie a payment to a milestone rather than a date. Enter the dates and amounts written in the agreement you actually signed, and confirm them with the vendor if anything is ambiguous.`,
    },
    {
      q: `Is a spreadsheet enough to track wedding vendors?`,
      a: `For many couples, yes. A spreadsheet handles the vendor and payment rows perfectly well, as long as it is the only one and it is updated when things change. A dedicated planning tool mainly helps by keeping the contracts, payments, conversation history and tasks attached to the same vendor instead of in four places.`,
    },
  ],

  related: {
    heading: "Where this fits in Agapaé",

    // The plan this article is one operational piece of, and the guide that
    // owns the headcount several vendor quantities are priced against. Both
    // are already linked once in the prose, at the point the subject changes;
    // naming them here as well is the cluster relationship rather than link
    // volume, and stays under the cap the test enforces.
    guides: ["wedding-planning-checklist", "how-to-organize-wedding-guest-list"],

    product: [
      {
        href: "/wedding-planning",
        label: "Wedding Planning",
        blurb:
          "See how the Planning Hub keeps your vendors, their contracts, what you owe them and what you have paid in one private space, alongside your tasks, documents and decisions. Available on the Heirloom plan.",
      },
    ],
  },
};

export default howToTrackWeddingVendorsPaymentsEn;
